import { useState, useEffect } from "react";
import { FaHotel, FaDoorOpen, FaKey, FaExclamationCircle } from "react-icons/fa";
import { useSearchParams, useNavigate } from "react-router-dom";
import { sendRequest } from "../../utils/api";
import Hotel from "../../models/Hotel";
import { validateSubdomain } from "../../utils/helper-function";
import Logo from "../../assets/hotel_logo.png";
import { setupSocket } from "../../socket";

export default function GuestLogin() {

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [hotel, setHotel] = useState<Hotel | null>(null)
    const initialRoom = searchParams.get("roomNumber");
    const initialCode = searchParams.get("passKey");

    const [error, setError] = useState("");

    const [room, setRoom] = useState(initialRoom || "");
    const [code, setCode] = useState(initialCode || "");

    const subdomain = validateSubdomain(navigate);

    useEffect(() => {
        // Check if we need to show the login form or ask for missing info
        // Three cases covered:
        // 1. Room number exists, but access code is missing
        // 2. Access code exists, but room number is missing
        // 3. Both room number and access code are missing
        const showLoginForm =
            (initialRoom && !initialCode) ||
            (initialCode && !initialRoom) ||
            (!initialRoom && !initialCode);

        const fetchHotel = async () => {
            if (showLoginForm) {
                try {
                    const { data } = await sendRequest("GET", `/hotels/${subdomain}`);
                    setHotel(data)
                } catch {

                }
            }
        }

        fetchHotel();
    }, []);

    const loginGuest = async (roomNumber: string, passKey: string) => {
        try {
            setError(""); // reset previous errors

            const data = await sendRequest(
                "POST",
                "/guests/login",
                {
                    passKey: passKey.trim(),
                    subdomain,
                    roomNumber,
                },
                false
            );

            const { token, guest, hotel, room, bill, booking, bookedRoom } = data;

            // Create Real-Time Socket Connection
            // setupSocket(hotel.id);

            // Set data in local storage.
            if (token) localStorage.setItem("authToken", token);
            localStorage.setItem(
                "hotel", JSON.stringify({
                    id: hotel?.hotelId,
                    subdomain: hotel?.subdomain,
                    name: hotel?.name,
                }));

            localStorage.setItem(
                "room", JSON.stringify({
                    id: room?.id,
                    number: room.number,
                    headline: room.headline,
                    cashPrice: room?.cashPrice,
                }));

            localStorage.setItem(
                "bookedRoom", JSON.stringify(bookedRoom)
            );

            localStorage.setItem(
                "bill", JSON.stringify({
                    id: bill?.id,
                }));

            localStorage.setItem(
                "booking", JSON.stringify({
                    id: booking?.id,
                }));

            localStorage.setItem(
                "user", JSON.stringify(guest)
            );
            // Set data in local storage.
            navigate("/guests/dashboard");


        } catch (err: any) {
            // Show error if passkey or room is wrong
            if (err.status === 401)
                setError("Incorrect room number or access code. Please try again");
            if (!err.status)
                setError("Unable to connect. Please check your network and try again.");
            else
                setError("Incorrect room number or access code. Please try again");
            // setError("Something went wrong. Please try again.");
        }
    };

    const handleLogin = (e: any) => {
        e.preventDefault();
        loginGuest(room, code);
    };

    /* ---------------- LOADING SCREEN ---------------- */
    if (initialRoom && initialCode) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100">

                <div className="text-center">

                    <div className="animate-spin rounded-full h-14 w-14 border-4 border-blue-500 border-t-transparent mx-auto"></div>

                    <p className="mt-4 text-gray-600 font-medium">
                        Connecting you to your room...
                    </p>

                </div>

            </div>
        );
    }

    /* ---------------- LOGIN FORM ---------------- */

    return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">

            <div className="bg-white w-full max-w-md rounded-2xl shadow-lg p-8">

                {/* Header */}

                <div className="text-center mb-6">

                    <div className="flex justify-center text-indigo-600 text-3xl mb-3">
                        <img
                            src={Logo}
                            alt="Logo"
                            className="w-20 h-20 mb-4 border-2 border-gray-300 rounded-full"
                        />
                    </div>

                    <h1 className="text-2xl font-semibold text-gray-800">
                        Guest Portal
                    </h1>

                    <p className="text-sm text-gray-500 mt-1">
                        {hotel?.name ?? ""}
                    </p>

                    {/* Error Message */}
                    {error && (
                        <div className="flex items-center bg-red-100 text-red-700 px-4 py-2 rounded-md mb-4">
                            <span className="mr-2">{FaExclamationCircle({})}</span>
                            <span>{error}</span>
                        </div>
                    )}

                </div>

                {/* Form */}

                <form onSubmit={handleLogin} className="space-y-4">

                    {/* Room Number: only show if not pre-filled */}
                    {!initialRoom && (
                        <div>
                            <label className="text-sm font-medium text-gray-600">Room Number</label>
                            <div className="flex items-center border rounded-lg px-3 py-2 mt-1">
                                <span className="text-gray-400 mr-2"> {FaDoorOpen({})} </span>
                                <input
                                    type="text"
                                    placeholder="Enter your room number"
                                    value={room}
                                    onChange={(e) => setRoom(e.target.value)}
                                    className="w-full outline-none"
                                    required
                                />
                            </div>
                        </div>
                    )}

                    {/* Access Code */}

                    <div>

                        <label className="text-sm font-medium text-gray-600">
                            Access Code
                        </label>

                        <div className="flex items-center border rounded-lg px-3 py-2 mt-1">

                            <span className="text-gray-400 mr-2">
                                {FaKey({})}
                            </span>

                            <input
                                type="password"
                                placeholder="Enter your access code"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                className="w-full outline-none"
                                required
                            />

                        </div>

                    </div>

                    {/* Login Button */}

                    <button
                        type="submit"
                        className="w-full bg-blue-500 text-white py-2 rounded-lg font-medium hover:bg-blue-600 transition"
                    >
                        Enter Guest Portal
                    </button>

                </form>

                {/* QR message */}

                <p className="text-xs text-gray-500 text-center mt-6">
                    You can also scan the QR code available in your room.
                </p>

            </div>

        </div>
    );
}
